import React from 'react';
import PropTypes from 'prop-types';
import withProfile from 'hocs/withProfile';
import { Deserializer } from 'jsonapi-serializer';

import { processErrorByAlert } from 'utils/getErrors';

import TaskMaterials from 'api/resources/taskMaterials';

import UserBan from 'admin-moderator/containers/UserBan';
import ReconnectingWebSocket from 'containers/ReconnectingWebSocket';

import Reward from 'admin-moderator/components/Reward';
import MaterialTypeTabs from 'admin-moderator/components/MaterialTypeTabs';
import UserMaterials from 'admin-moderator/components/UserMaterials';
import SocialLinks from 'admin-moderator/components/SocialLinks';
import Tab from 'components/Tab';
import Avatar from 'components/Avatar';
import ChatRoomFinishButton from 'chats/ChatRoomFinishButton';
import ChatRoomOpenBodyContainer from 'chats/ChatRoomOpenBodyContainer';

import Name from 'styled/ItemCard/Name';
import SecondaryButton from 'styled/Buttons/SecondaryButton';

import UserPanelStyled from './UserPanelStyled';
import Header from './Header';
import UserInfo from './UserInfo';
import Phone from './Phone';
import BanWrapper from './BanWrapper';
import Body from './Body';
import TabsWrapper from './TabsWrapper';

const TABS = [{
  id: 'techRoom',
  label: 'Чат техподдержки',
}, {
  id: 'moderatorRoom',
  label: 'Чат с пользователем',
}];

const PAGE_SIZE = 60;

export class UserPanel extends React.PureComponent {
  state = {
    type: 'materials',

    roomType: TABS[0].id,

    materialType: 'image',
    materials: [],
    loading: false,
    materialError: false,
    pageNumber: 0,
    hasMore: false,
  }

  componentDidMount() {
    this.loadMaterials();
  }

  get activeChatRoom() {
    return this.props[this.state.roomType];
  }

  handleChatsOpenClick = () => {
    this.setState({ type: 'chats' });
  }

  handleMaterialsOpenClick = () => {
    this.setState({ type: 'materials' });
  }

  handleTabChange = (activeTab) => {
    this.setState({ roomType: activeTab.id });
  }

  loadMaterials = async () => {
    this.setState({ loading: true });
    try {
      const response = await TaskMaterials.getAll({
        page: {
          number: this.state.pageNumber,
          size: PAGE_SIZE,
        },
        filter: {
          user_id: this.props.id,
          type: this.state.materialType || undefined,
        },
        include: 'file,file.cover',
        fields: {
          material: 'status,file',
          image: 'url,status',
          video: 'duration,url,original_url,status,cover',
        },
      });

      // TODO: вынести в утилиты все valueForRelationship
      const materials = await new Deserializer({
        keyForAttribute: 'camelCase',
        video: {
          valueForRelationship: (relationship, included) => ({
            ...included,
            type: relationship.type,
          }),
        },
        image: {
          valueForRelationship: (relationship, included) => ({
            ...included,
            type: relationship.type,
          }),
        },
      }).deserialize(response);

      this.setState(prevState => ({
        materials: prevState.materials.concat(materials),
        loading: false,
        hasMore: materials.length === PAGE_SIZE,
        pageNumber: prevState.pageNumber + 1,
      }));
    } catch (err) {
      // TODO: ошибки загрузки выводить в интерфейс
      processErrorByAlert(err);
      this.setState({ loading: false, materialError: true });
    }
  }

  handleFilterChange = ({ currentTarget }) => {
    if (this.state.loading) return;

    this.setState({
      materialType: currentTarget.name,
      pageNumber: 0,
      materials: [],
    }, this.loadMaterials);
  }

  render() {
    return (
      <UserPanelStyled>
        {/* TODO: переверстать в соответствии с ChatRoomOpenHeader */}

        <Header>
          <Avatar
            size="56px"
            name={this.props.fullName}
            url={this.props.avatarUrl}
            hasBorder
          />

          <UserInfo>
            <Name style={{ marginRight: '40px' }}>{this.props.fullName}</Name>

            <Phone>{this.props.phone}</Phone>

            <SocialLinks socialLinks={this.props.socialLinks} />

            {this.state.type === 'chats' &&
              this.activeChatRoom &&
              this.activeChatRoom.roomType === 'moderator_chat' &&
              this.activeChatRoom.locked &&
              this.activeChatRoom.lockedEmployeeId === this.props.profile.id && (
                <ChatRoomFinishButton
                  id={this.activeChatRoom && this.activeChatRoom.id}
                  onFinished={this.props.onChatRoomFinished}
                />
              )
            }

            {this.state.type === 'materials' && (
              <SecondaryButton
                type="button"
                onClick={this.handleChatsOpenClick}
                style={{ marginTop: '18px' }}
              >
                Посмотреть чаты
              </SecondaryButton>
            )}

            {this.state.type === 'chats' && (
              <SecondaryButton
                type="button"
                onClick={this.handleMaterialsOpenClick}
                style={{ marginTop: '18px' }}
              >
                Посмотреть материалы
              </SecondaryButton>
            )}
          </UserInfo>

          <Reward id={this.props.id} />

          <BanWrapper>
            <UserBan
              userId={this.props.id}
              isBanned={this.props.isBanned}
              onBan={this.props.onBan}
              fullName={this.props.fullName}
            />
          </BanWrapper>
        </Header>

        <Body>
          {this.state.type === 'materials' && (
            <React.Fragment>
              <MaterialTypeTabs
                id={this.props.id}
                filter={this.state.materialType}
                onFilterChange={this.handleFilterChange}
                imagesCount={this.props.imagesCount}
                videosCount={this.props.videosCount}
                acceptedImageCount={this.props.acceptedImageCount}
                acceptedVideoCount={this.props.acceptedVideoCount}
              />

              <UserMaterials
                materials={this.state.materials}
                onWaypointEnter={this.loadMaterials}
                loading={this.state.loading}
                hasMore={this.state.hasMore}
                error={this.state.materialError}
              />
            </React.Fragment>
          )}

          {this.state.type === 'chats' && (
            <React.Fragment>
              <TabsWrapper>
                {TABS.map(tab => (
                  <Tab
                    key={tab.label}
                    tab={tab}
                    active={tab.id === this.state.roomType}
                    onChange={this.handleTabChange}
                  />
                ))}
              </TabsWrapper>

              <ReconnectingWebSocket onMessage={this.props.onAdminNotificationsMessage} path="/_ws/c/admin/notifications" />

              <ReconnectingWebSocket path="/_ws/c/chats/messages">
                {socket => (
                  <ChatRoomOpenBodyContainer
                    id={this.activeChatRoom && this.activeChatRoom.id}

                    startChatAutoFocus
                    hasNewMessages={this.activeChatRoom && this.activeChatRoom.hasNewMessages}
                    roomType={this.activeChatRoom && this.activeChatRoom.roomType}
                    locked={this.activeChatRoom && this.activeChatRoom.locked}
                    lockedEmployeeId={this.activeChatRoom && this.activeChatRoom.lockedEmployeeId}
                    lockedEmployeeFullname={this.activeChatRoom && this.activeChatRoom.lockedEmployeeFullname}
                    userId={this.props.id}

                    onChatRoomStarted={this.props.onChatRoomStarted}
                    onChatRoomFinished={this.props.onChatRoomFinished}

                    socket={socket}
                  />
                )}
              </ReconnectingWebSocket>
            </React.Fragment>
          )}
        </Body>
      </UserPanelStyled>
    );
  }
}

UserPanel.propTypes = {
  id: PropTypes.string.isRequired,
  avatarUrl: PropTypes.string,
  phone: PropTypes.string,
  isBanned: PropTypes.bool,
  fullName: PropTypes.string,
  imagesCount: PropTypes.number,
  videosCount: PropTypes.number,
  acceptedImageCount: PropTypes.number,
  acceptedVideoCount: PropTypes.number,
  socialLinks: PropTypes.arrayOf(PropTypes.shape({
    provider: PropTypes.oneOf(['vkontakte', 'odnoklassniki', 'facebook', 'twitter', 'instagram', 'google']),
    socialLink: PropTypes.string,
  })),

  onBan: PropTypes.func,
  onChatRoomStarted: PropTypes.func,
  onChatRoomFinished: PropTypes.func,
  onAdminNotificationsMessage: PropTypes.func,

  profile: PropTypes.shape({ id: PropTypes.string }),
};

UserPanel.defaultProps = {
  avatarUrl: '',
  phone: '',
  isBanned: false,
  fullName: '',
  imagesCount: 0,
  videosCount: 0,
  acceptedImageCount: 0,
  acceptedVideoCount: 0,
  socialLinks: [],

  onBan: () => {},
  onChatRoomStarted: () => {},
  onChatRoomFinished: () => {},
  onAdminNotificationsMessage: () => {},

  profile: {},
};

export default withProfile(UserPanel);
